/**
 * One-time move of the account rows into the device registry.
 *
 * Before per-account databases there was a single `fahrtenbuch-pro` holding
 * both the accounts and the books. The accounts now live in their own registry
 * so the login screen can list drivers without unlocking anything, and each
 * driver's books live in a database of their own.
 *
 * The existing install keeps `fahrtenbuch-pro` as its books rather than having
 * them copied into a differently named database: the rows are encrypted and
 * hash-chained, and a bulk rewrite is a real risk taken for a cosmetic gain.
 * The account row records where its books are, so the two naming schemes
 * coexist indefinitely without a special case anywhere else.
 */

import Dexie from 'dexie';
import type { AccountRecord } from '@/crypto/vault';
import { DB_NAME, accountsDb } from './schema';

/**
 * Copy any legacy accounts into the registry.
 *
 * Safe to call on every boot: it does nothing once the registry is populated,
 * and nothing on an install that never had the old layout.
 */
export async function migrateLegacyAccounts(): Promise<void> {
  if ((await accountsDb.accounts.count()) > 0) return;
  if (!(await Dexie.exists(DB_NAME))) return;

  // Opened without a schema so Dexie reads whatever version is on disk. A
  // declared schema here would trigger an upgrade before the rows are safely
  // copied across.
  const legacy = new Dexie(DB_NAME);
  try {
    await legacy.open();
    if (!legacy.tables.some((table) => table.name === 'accounts')) return;

    // The stored rows predate `dbName`, so the legacy shape says so.
    const accounts = (await legacy.table('accounts').toArray()) as Array<
      Omit<AccountRecord, 'dbName'> & { dbName?: string }
    >;
    if (accounts.length === 0) return;

    await accountsDb.accounts.bulkPut(
      accounts.map((account) => ({ ...account, dbName: account.dbName ?? DB_NAME })),
    );
  } finally {
    legacy.close();
  }
}
